import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import AdminNavbar from './AdminNavbar';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiMonitor, FiActivity, FiDollarSign, FiTrendingUp, FiTrendingDown, FiAlertTriangle } = FiIcons;

const AdminDashboard = () => {
  const [stats] = useState({
    totalUsers: 1247,
    activeMonitors: 3456,
    monthlyRevenue: 89500,
    supportTickets: 23
  });

  const userGrowthOption = {
    title: {
      text: 'User Growth',
      textStyle: { color: '#ffffff' }
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      axisLabel: { color: '#ffffff80' }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#ffffff80' }
    },
    series: [{
      data: [820, 932, 901, 934, 1290, 1330],
      type: 'line',
      smooth: true,
      itemStyle: { color: '#8b5cf6' }
    }]
  };

  const revenueOption = {
    title: {
      text: 'Monthly Revenue',
      textStyle: { color: '#ffffff' }
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      axisLabel: { color: '#ffffff80' }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#ffffff80' }
    },
    series: [{
      data: [65000, 72000, 68000, 75000, 82000, 89500],
      type: 'bar',
      itemStyle: { color: '#06b6d4' }
    }]
  };

  const recentActivities = [
    { id: 1, action: 'New user registration: john.doe@company.com', time: '5 minutes ago', type: 'user' },
    { id: 2, action: 'Support ticket #1234 resolved', time: '15 minutes ago', type: 'support' },
    { id: 3, action: 'Payment received: $299 from Acme Corp', time: '1 hour ago', type: 'payment' },
    { id: 4, action: 'System maintenance completed', time: '2 hours ago', type: 'system' }
  ];

  return (
    <div className="min-h-screen">
      <AdminNavbar />
      
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-white mb-2">
              Admin Dashboard
            </h1>
            <p className="text-white/70">
              Monitor and manage the SignageCloud platform
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiUsers} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">{stats.totalUsers.toLocaleString()}</span>
              </div>
              <h3 className="text-white/80 font-medium">Total Users</h3>
              <div className="flex items-center mt-2">
                <SafeIcon icon={FiTrendingUp} className="text-green-400 text-sm mr-1" />
                <span className="text-green-400 text-sm">+12% from last month</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiMonitor} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">{stats.activeMonitors.toLocaleString()}</span>
              </div>
              <h3 className="text-white/80 font-medium">Active Monitors</h3>
              <div className="flex items-center mt-2">
                <SafeIcon icon={FiTrendingUp} className="text-green-400 text-sm mr-1" />
                <span className="text-green-400 text-sm">+8% from last month</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiDollarSign} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">${stats.monthlyRevenue.toLocaleString()}</span>
              </div>
              <h3 className="text-white/80 font-medium">Monthly Revenue</h3>
              <div className="flex items-center mt-2">
                <SafeIcon icon={FiTrendingUp} className="text-green-400 text-sm mr-1" />
                <span className="text-green-400 text-sm">+15% from last month</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiActivity} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">{stats.supportTickets}</span>
              </div>
              <h3 className="text-white/80 font-medium">Open Tickets</h3>
              <div className="flex items-center mt-2">
                <SafeIcon icon={FiTrendingDown} className="text-red-400 text-sm mr-1" />
                <span className="text-red-400 text-sm">-5% from last month</span>
              </div>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* User Growth Chart */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6"
            >
              <ReactECharts 
                option={userGrowthOption} 
                style={{ height: '300px' }}
                theme="dark"
              />
            </motion.div>

            {/* Revenue Chart */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6"
            >
              <ReactECharts 
                option={revenueOption} 
                style={{ height: '300px' }}
                theme="dark"
              />
            </motion.div>
          </div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6"
          >
            <h2 className="text-xl font-semibold text-white mb-6">Recent Activity</h2>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    activity.type === 'user' ? 'bg-blue-400' :
                    activity.type === 'support' ? 'bg-green-400' :
                    activity.type === 'payment' ? 'bg-purple-400' :
                    'bg-orange-400'
                  }`}></div>
                  <div className="flex-1">
                    <p className="text-white/90">{activity.action}</p>
                    <p className="text-white/50 text-sm mt-1">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;